import * as React from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";

import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../../../../components/ui/dialog";

import { ViewContext } from "components/lib";
import { stateOptions } from "data";
import { useEffect } from "react";
import { UpsertDriverDialog } from "./UpsertDriverDialog";
import { ExistingDriversPicker } from "./ExistingDriversPicker";
import { FieldError } from "../FieldError";

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from "../../../../components/ui/select";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Controller } from "react-hook-form";
import { SwitchInput } from "../SwitchInput";
import { YearMonthPicker } from "../operations/YearMonthPicker";

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

// - Do you include company rules and procedures into driver training? (y/n)
// - Daily vehicle inspections? (y/n)
// - Equipment familiarization? (y/n)
// - Route familiarization? (y/n)
// - Emergency procedures? (y/n)
// - Accident reporting procedures? (y/n)
// - Written driver safety manual? (y/n)
const driverTrainingSwitchInputs = [
  {
    id: "company-rules-procedures",
    stepName: "step5.companyRulesProcedures",
    label:
      "Do you include company rules and procedures into driver training?",
  },
  {
    id: "daily-vehicle-inspections",
    stepName: "step5.dailyVehicleInspections",
    label:
      "Daily vehicle inspections?",
  },
  {
    id: "equipment-familiarization",
    stepName: "step5.equipmentFamiliarization",
    label:
      "Equipment familiarization?",
  },
  {
    id: "route-familiarization",
    stepName: "step5.routeFamiliarization",
    label:
      "Route familiarization?",
  },
  {
    id: "emergency-procedures",
    stepName: "step5.emergencyProcedures",
    label:
      "Emergency procedures?",
  },
  {
    id: "accident-reporting-procedures",
    stepName: "step5.accidentReportingProcedures",
    label:
      "Accident reporting procedures?",
  },
  {
    id: "written-driver-safety-manual",
    stepName: "step5.writtenDriverSafetyManual",
    label:
      "Written driver safety manual?",
  },
  {
    id: "mvrs-checked-annually",
    stepName: "step5.mvrsCheckedAnnually",
    label:
      "Are MVRs checked annually?",
  },
];

export function Drivers({ methods, save }) {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
    register,
  } = methods;
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "step5.drivers",
  });

  const context = React.useContext(ViewContext);
  const location = useLocation();
  const companyId = location?.state?.data?.company_id;

  // Existing drivers for "Select Existing"
  const [existingDrivers, setExistingDrivers] = React.useState([]);
  const [selectOpen, setSelectOpen] = React.useState(false);
  const [chooserOpen, setChooserOpen] = React.useState(false);
  const [upsertOpen, setUpsertOpen] = React.useState(false);
  const [mode, setMode] = React.useState("create"); // "create" | "edit"
  const [editingIndex, setEditingIndex] = React.useState(null);

  const step1Carrier = useWatch({ control, name: "step1.carrier" });
  const step5Carrier = useWatch({ control, name: "step5.carrier" });
  const cdlExperience = useWatch({ control, name: "step5.cdlExperience" });

  React.useEffect(() => {
    register("step5.carrier");
  }, [register]);
  

  // Load drivers into the form (parity with old impl) when opening quote for a company
  React.useEffect(() => {
    if (companyId) {
      axios
        .get(`/api/driver/all?company_id=${companyId}`)
        .then((r) => {
          const list = (r?.data?.data?.data ?? []).map((driver) => {
            const licenseState =
              stateOptions.find((s) => s.value === driver.license_state)
                ?.value ??
              stateOptions.find((s) => s.label === driver.license_state)
                ?.value ??
              driver.license_state;

            return {
              id: driver.id,
              driverId: driver.id,
              firstName: driver.name?.split(" ")[0] ?? "",
              lastName: driver.name?.split(" ")[1] ?? "",
              dateOfBirth: driver.date_of_birth?.split("T")[0],
              dateOfHire: driver.date_of_hire
                ? driver.date_of_hire.split("T")[0]
                : null,
              licenseNumber: driver.license_number,
              licenseState,
              yearsExperience: Number(driver.years_of_experience ?? 0),
              accidents: Number(driver.accidents ?? 0),
              violations: Number(driver.violations ?? 0),
              suspensions: Number(driver.suspensions ?? 0),
              eligibility: "Covered",
              exclude: !!driver.exclude_from_policy,
              status: driver.status ?? "active",
              backImg: driver.back_license,
              frontImg: driver.front_license
            };
          });

          if (!location?.state?.data?.quote_id) {
            setValue("step5.drivers", list);
          }
          setExistingDrivers(list); // Use same as existing list for simplicity
        })
        .catch((err) => {
          console.error(err);
          context.notification?.show?.(
            "Error on load company drivers",
            "error",
            true
          );
        });
    }
  }, [companyId]);

  React.useEffect(() => {
    const list = Array.isArray(step1Carrier)
      ? step1Carrier
      : step1Carrier ? [step1Carrier] : [];
    const normalized = list.map((s) => (s ?? ""));

    if (!arraysEqual(normalized, step5Carrier ?? [])) {
      setValue("step5.carrier", normalized, { shouldDirty: false, shouldValidate: true });
    }
  }, [step1Carrier, step5Carrier]);

  const openCreate = () => {
    setMode("create");
    setEditingIndex(null);
    setUpsertOpen(true);
  };

  const openEdit = (idx) => {
    setMode("edit");
    setEditingIndex(idx);
    setUpsertOpen(true);
  };

  const addFromExisting = (d) => {
    // avoid duplicates by id + license number
    const current = getValues("step5.drivers") ?? [];
    if (
      current.some(
        (x) =>
          (d.driverId && x.driverId === d.driverId) ||
          x.licenseNumber === d.licenseNumber
      )
    ) {
      context.notification?.show?.("Driver already added.", "error", true);
      return;
    }
    append(d);
    setSelectOpen(false);
  };

  const onUpsertDone = (driver, shouldRemove = false) => {
    methods.trigger();

    if (mode === "create") {
      append(driver);
    } else if (editingIndex != null) {
      if (shouldRemove) {
        remove(editingIndex);
      } else {
        update(editingIndex, driver);
      }
    }
  };

  const collectErrors = (errNode, prefix = "") => {
    if (!errNode) return [];

    if (errNode.message) return [errNode.message];

    if (Array.isArray(errNode)) {
      return errNode.flatMap((e, idx) => collectErrors(e, `${prefix}[${idx}]`));
    }

    if (typeof errNode === "object") {
      return Object.values(errNode).flatMap((e) => collectErrors(e, prefix));
    }

    return [];
  };

  const errorMessages = collectErrors(errors.step5?.drivers);

  useEffect(() => {
    methods.trigger();
  }, []);

  const hasAccelerant = step5Carrier?.includes("Accelerant");

  React.useEffect(() => {
    if (!hasAccelerant) {
      methods.clearErrors([
        "step5.wayOfCompensation",
        "step5.driverHomeDaysPerWeek",
        "step5.minimumAgeOfDrivers",
        "step5.violationsPastSixMonths",
        "step5.disciplinaryActionForDrivers",
        "step5.howOftenAreMandatorySafetyMeetingsHeld",
        "step5.cdlExperience",
      ]);
    }
  }, [hasAccelerant, methods]);

  React.useEffect(() => {
    if (!hasAccelerant) return;
    if (
      !cdlExperience ||
      typeof cdlExperience !== "object" ||
      (cdlExperience.year == null && cdlExperience.month == null)
    ) {
      setValue("step5.cdlExperience", { year: cdlExperience?.year ?? "", month: cdlExperience?.month ?? "" }, { shouldValidate: true });
    }
    methods.trigger("step5.cdlExperience");
  }, [hasAccelerant, cdlExperience?.year, cdlExperience?.month]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">Drivers</h2>
        <Button
          className="bg-red-600 hover:bg-red-700 text-white"
          onClick={() => setChooserOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Driver
        </Button>
      </div>

      {/* Cards list */}
      {fields?.length > 0 && (
        <div className="space-y-2">
          {fields.map((d, i) => (
            <div
              key={d.id ?? `${d.licenseNumber}-${i}`}
              className="rounded-md border p-3 text-sm text-slate-700 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div>
                  <div className="font-medium">
                    {d.firstName} {d.lastName}
                    {d.status === "inactive" && (
                      <span className="ml-2 text-xs rounded bg-gray-100 text-gray-700 px-2 py-0.5">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Lic #{d.licenseNumber} — {d.licenseState} &middot; DoB{" "}
                    {(d.dateOfBirth instanceof Date
                      ? d.dateOfBirth.toLocaleDateString()
                      : d.dateOfBirth) || "N/A"}
                    &middot; Exp {d.yearsExperience}{" "}
                    {d.yearsExperience === 1 ? "year" : "years"}
                    {d.dateOfHire ? ` DoH ${d.dateOfBirth instanceof Date ? d.dateOfHire.toLocaleDateString() : d.dateOfHire}` : ""}
                  </div>
                </div>
                {d.exclude && (
                  <span className="text-xs rounded bg-yellow-100 text-yellow-800 px-2 py-0.5">
                    Excluded
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(i)}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={async () => {
                    try {
                      // await axios.delete(`/api/driver/${d.driverId}`);
                      const drivers = existingDrivers.filter((f) => f.driverId !== d.driverId);
                      setExistingDrivers([...drivers, d])
                      remove(i);
                      await save();
                    } catch (e) {
                      console.error(e);
                      context.notification?.show?.(
                        "Error deleting driver",
                        "error",
                        true
                      );
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <FieldError step={"step5"} name="step5.drivers" />

      {/* Dialog 1: chooser (Select existing | Create new) */}
      <Dialog open={chooserOpen} onOpenChange={setChooserOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Create a new driver or choose from your existing drivers?
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setChooserOpen(false);
                setSelectOpen(true);
              }}
            >
              Select Existing
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                setChooserOpen(false);
                openCreate();
              }}
            >
              Create New Driver
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog 2: Select Existing */}
      <Dialog open={selectOpen} onOpenChange={setSelectOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Select Existing Driver</DialogTitle>
          </DialogHeader>

          <ExistingDriversPicker
            existingDrivers={existingDrivers}
            selected={fields}
            onPick={addFromExisting}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog 3: Create/Edit */}
      <UpsertDriverDialog
        open={upsertOpen}
        onOpenChange={setUpsertOpen}
        mode={mode}
        initial={editingIndex != null ? fields[editingIndex] : undefined}
        companyId={companyId}
        onDone={onUpsertDone}
        carriers={step5Carrier}
      />

      {hasAccelerant && <div className="space-y-2">
        <Label>How are drivers compensated?</Label>
        <Controller
          control={control}
          name="step5.wayOfCompensation"
          render={({ field, fieldState }) => (
            <>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full !h-10 py-2 px-3">
                  <SelectValue placeholder="Way of Compensation" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  <SelectGroup>
                    <SelectLabel>Way of Compensation</SelectLabel>
                    {[
                      { label: "Hourly", value: "Hourly" },
                      { label: "% of Load", value: "Percent Of Load" },
                      { label: "Mileage", value: "Mileage" },
                      { label: "By Trip", value: "By Trip" },
                    ].map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error && (
                <p className="text-xs text-red-600">
                  {fieldState.error.message}
                </p>
              )}
            </>
          )}
        />
        <div className="rounded-lg border p-4 space-y-4">
          <h3 className="text-lg font-semibold">Driver Training</h3>
          {driverTrainingSwitchInputs.map(({ id, stepName, label }) => (
            <SwitchInput
              key={id}
              control={control}
              id={id}
              stepName={stepName}
              stepId="step5"
              label={label}
            />
          ))}
        </div>
        <div className="rounded-lg border p-4 space-y-4">
          <h3 className="text-lg font-semibold">Driver and Operation Safety</h3>
          <div className="space-y-2">
            <Label htmlFor="driverHomeDaysPerWeek">How many days are drivers home per week?</Label>
            <Input
              id="driverHomeDaysPerWeek"
              type="text"
              placeholder="Enter..."
              {...register("step5.driverHomeDaysPerWeek")}
            />
            <FieldError name="step5.driverHomeDaysPerWeek" step="step5" control={control} />
          </div>
          <div className="space-y-2">
            <SwitchInput
              key={'drivers-dot-compliant'}
              control={control}
              id={'drivers-dot-compliant'}
              stepName={'step5.driversDotCompliant'}
              stepId="step5"
              label={"Are your drivers DOT compliant?"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="minimumAgeOfDrivers">Minimum age of drivers</Label>
            <Input
              id="minimumAgeOfDrivers"
              type="text"
              placeholder="Enter..."
              {...register("step5.minimumAgeOfDrivers")}
            />
            <FieldError name="step5.minimumAgeOfDrivers" step="step5" control={control} />
          </div>
          <div className="space-y-2">
            <Label>Minimum CDL Experience</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="cdlExperience.year" className="text-xs text-muted-foreground">Years</Label>
                <Input
                  id="cdlExperience.year"
                  type="number"
                  min={0}
                  placeholder="Years"
                  {...register("step5.cdlExperience.year")}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="cdlExperience.month" className="text-xs text-muted-foreground">Months</Label>
                <Input
                  id="cdlExperience.month"
                  type="number"
                  min={0}
                  max={11}
                  placeholder="Months"
                  {...register("step5.cdlExperience.month")}
                />
              </div>
            </div>
            <FieldError name="step5.cdlExperience" step="step5" control={control} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="violationsPastSixMonths">How many violations are your drivers allowed to have in the past 36 months?</Label>
            <Input
              id="violationsPastSixMonths"
              type="text"
              placeholder="Enter..."
              {...register("step5.violationsPastSixMonths")}
            />
            <FieldError name="step5.violationsPastSixMonths" step="step5" control={control} />
          </div>
          <div className="space-y-2">
            <SwitchInput
              key={'zeroTolerancePolicyForMajorViolations'}
              control={control}
              id={'zeroTolerancePolicyForMajorViolations'}
              stepName={'step5.zeroTolerancePolicyForMajorViolations'}
              stepId="step5"
              label={"Is there a zero tolerance policy for major violations?"}
            />
          </div>
          <div className="space-y-2">
            <SwitchInput
              key={'safetyRewardsProgram'}
              control={control}
              id={'safetyRewardsProgram'}
              stepName={'step5.safetyRewardsProgram'}
              stepId="step5"
              label={"Does insured have a safety rewards program?"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="disciplinaryActionForDrivers">What is the disciplinary action for drivers that develop unacceptable records?</Label>
            <Input
              id="disciplinaryActionForDrivers"
              type="text"
              placeholder="Enter..."
              {...register("step5.disciplinaryActionForDrivers")}
            />
            <FieldError name="step5.disciplinaryActionForDrivers" step="step5" control={control} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="howOftenAreMandatorySafetyMeetingsHeld">How often are mandatory safety meetings held?</Label>
            <Input
              id="howOftenAreMandatorySafetyMeetingsHeld"
              type="text"
              placeholder="Enter..."
              {...register("step5.howOftenAreMandatorySafetyMeetingsHeld")}
            />
            <FieldError name="step5.howOftenAreMandatorySafetyMeetingsHeld" step="step5" control={control} />
          </div>
          {[{
            id: "maintain-accident-register",
            stepName: "step5.maintainAccidentRegister",
            label:
              "Do you maintain an accident register?",
          },
          {
            id: "written-maintenance-program",
            stepName: "step5.writtenMaintenanceProgram",
            label:
              "Does the insured have a written maintenance program?",
          },
          {
            id: "insured-do-their-own-maintenance",
            stepName: "step5.insuredDoTheirOwnMaintenance",
            label:
              "Does the insured do their own maintenance?",
          }].map(({ id, stepName, label }) => (
            <SwitchInput
              key={id}
              control={control}
              id={id}
              stepName={stepName}
              stepId="step5"
              label={label}
              className="space-y-2"
            />
          ))}
        </div>
      </div>}
    </div>
  );
}
